import { memo, useState } from "react";
import {
  FloatingPortal,
  autoUpdate,
  offset,
  useFloating,
  useHover,
  useInteractions,
  useRole,
} from "@floating-ui/react";
import { Handle, Position } from "@xyflow/react";
import { Paragraph } from "@unlikelyai-magic/ui/typography";

interface EditSplitNodeData {
  operation: string;
  explanation: string;
}

interface EditSplitNodeProps {
  id: string;
  data: EditSplitNodeData;
  isConnectable: boolean;
}

export const EditTraceNode = memo(
  ({ id, data, isConnectable }: EditSplitNodeProps) => {
    const [open, setOpen] = useState(false);
    const { refs, floatingStyles, context } = useFloating({
      open,
      onOpenChange: setOpen,
      placement: "top",
      middleware: [offset(8)],
      whileElementsMounted: autoUpdate,
    });
    const hover = useHover(context, { move: false, restMs: 50 });
    const role = useRole(context, { role: "tooltip" });
    const { getReferenceProps, getFloatingProps } = useInteractions([
      hover,
      role,
    ]);

    return (
      <>
        <Handle
          type="target"
          position={Position.Top}
          isConnectable={isConnectable}
          className="opacity-0"
        />
        <div
          ref={refs.setReference}
          {...getReferenceProps()}
          className="px-4 py-3 rounded-lg border-2 border-dashed border-purple-400 bg-purple-50 min-w-[200px] max-w-[400px]"
        >
          <h5>Operation: {data.operation}</h5>
        </div>
        <Handle
          type="source"
          position={Position.Bottom}
          isConnectable={isConnectable}
          className="opacity-0"
        />

        {open && (
          <FloatingPortal>
            <div
              ref={refs.setFloating}
              style={floatingStyles}
              {...getFloatingProps()}
              className="max-w-md rounded-lg p-3 bg-white border border-gray-200 shadow-lg z-[9999]"
            >
              <div className="text-sm text-gray-700 mb-2">
                {data.explanation}
              </div>
            </div>
          </FloatingPortal>
        )}
      </>
    );
  }
);

EditTraceNode.displayName = "EditTraceNode";
